import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import api, { setUnauthorizedHandler } from "@/lib/api";
import { browserTimezone } from "@/lib/timeago";
import { setAccountTimezone } from "@/lib/timezones";

const AuthContext = createContext(null);

// Session-scoped sentinel: one browser-timezone probe per tab session, never per page load.
const TZ_PROBE_KEY = "so_tz_probe";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [entitlements, setEntitlements] = useState({});
  const [loading, setLoading] = useState(true);
  const redirectingRef = useRef(false);

  // Publish the member's chosen zone so every form and time display can prefill from it.
  useEffect(() => { setAccountTimezone(user?.timezone); }, [user?.timezone]);

  // OAD-127 — fill an EMPTY account timezone from the browser's resolved IANA zone, once per
  // browser session, for whichever path signed the member in (password, Google, MFA, restored
  // session). We never look for a zone we already have, so there is no drift comparison and no
  // travel tracking: a member with a stored zone never triggers this at all.
  //
  // The sentinel is set only once the SERVER has resolved the attempt — a 2xx (stored or a
  // no-op) or a 400 (this browser's value will never be accepted). A network error, 5xx or 429
  // deliberately leaves it unset so the next session can retry.
  const tzProbeRef = useRef(false);
  useEffect(() => {
    if (!user?.user_id || user.timezone) return;
    if (tzProbeRef.current) return;
    let sentinel = false;
    try { sentinel = window.sessionStorage.getItem(TZ_PROBE_KEY) === "1"; } catch { sentinel = false; }
    if (sentinel) return;
    const detected = browserTimezone();
    if (!detected || !detected.includes("/")) return;   // plausible IANA-style values only
    tzProbeRef.current = true;
    const mark = () => { try { window.sessionStorage.setItem(TZ_PROBE_KEY, "1"); } catch { /* private mode */ } };
    api.post("/account/timezone/detected", { timezone: detected })
      .then(({ data }) => {
        mark();
        if (data?.stored && data?.timezone) {
          setUser((u) => (u && !u.timezone ? { ...u, timezone: data.timezone } : u));
        }
      })
      .catch((e) => {
        if (e?.response?.status === 400) mark();        // unusable value — retrying cannot help
        tzProbeRef.current = false;                    // transient failure: allow a later retry
      });
  }, [user?.user_id, user?.timezone]);

  const loadEntitlements = useCallback(async () => {
    try {
      const { data } = await api.get("/me/entitlements");
      setEntitlements(data.entitlements || {});
    } catch {
      setEntitlements({});
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadEntitlements();
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [loadEntitlements]);

  useEffect(() => {
    // If returning from Google OAuth, let AuthCallback exchange the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // SEC-09: when any PROTECTED request 401s (session invalidated server-side —
  // self-delete, admin delete, TTL expiry, password reset elsewhere), clear auth
  // state (this also tears down the realtime socket via the `user` effect) and,
  // if we're on an in-app route, hard-redirect to the login page so no page is
  // left hanging on a spinner or showing a zombie "logged-in" shell.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      setEntitlements({});
      const path = window.location.pathname;
      const onProtected =
        path.startsWith("/dashboard") ||
        ["/welcome", "/verify-email", "/set-password"].some(
          (p) => path === p || path.startsWith(`${p}/`)
        );
      if (onProtected && !redirectingRef.current) {
        redirectingRef.current = true;
        window.location.replace("/login?session=expired");
      }
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.pending_deletion) return data;         // caller shows the restore prompt
    if (data.mfa_required) return data;             // caller shows the MFA challenge (no session yet)
    if (data.password_change_required) return data; // admin-created temp pw — forced change (no session yet)
    if (data.temporary_password_expired) return data; // temp pw expired — caller shows the notice
    setUser(data.user);
    await loadEntitlements();
    return data.user;
  };

  // Complete the mandatory first-login password change for an admin-created account.
  // Exchanges the in-memory single-use change token + the new password for a fresh
  // session (backend sets the cookie). The change token is never a session itself.
  const completeRequiredPasswordChange = async (changeToken, password) => {
    const { data } = await api.post("/auth/required-password-change", {
      change_token: changeToken, password,
    });
    setUser(data.user);
    await loadEntitlements();
    return data.user;
  };

  const completeMfaChallenge = async (mfaToken, code, isBackup) => {
    const { data } = await api.post("/auth/mfa/challenge", {
      mfa_token: mfaToken, code, is_backup: !!isBackup,
    });
    setUser(data.user);
    await loadEntitlements();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", { timezone: browserTimezone() || undefined, ...payload });
    setUser(data.user);
    await loadEntitlements();
    return data.user;
  };

  const completeGoogle = async (sessionId) => {
    const { data } = await api.get("/auth/google/session", {
      headers: { "X-Session-ID": sessionId },
    });
    if (data.pending_deletion) return data;  // caller shows the restore prompt
    setUser(data.user);
    await loadEntitlements();
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(null);
    setEntitlements({});
  };

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      /* ignore */
    }
  }, []);

  const value = {
    user, setUser, entitlements, loading,
    login, register, completeGoogle, completeMfaChallenge, completeRequiredPasswordChange, logout, refreshUser, checkAuth,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
export const useEntitlement = (key) => {
  const ctx = useContext(AuthContext);
  return !!(ctx && ctx.entitlements && ctx.entitlements[key]);
};
